"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Dialog from "@mui/material/Dialog"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import { Icons } from "@src/components"

const MIN_ZOOM = 1
const MAX_ZOOM = 4
const ZOOM_STEP = 0.5
/** Target zoom level when user first presses + (req: ≥2× for texture) */
const DEFAULT_ZOOM_IN = 2

type Props = {
  open: boolean
  onClose: () => void
  src: string
  alt?: string
  images?: string[]
  initialIndex?: number
}

type Point = { x: number; y: number }

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const getDistance = (a: Point, b: Point) =>
  Math.hypot(b.x - a.x, b.y - a.y)

const ProductImageLightbox = (props: Props) => {
  const { open, onClose, src, alt = "", images, initialIndex = 0 } = props

  const gallery = (images?.filter(Boolean)?.length ? images.filter(Boolean) : src ? [src] : []) as string[]
  const [index, setIndex] = useState(initialIndex)
  const [zoom, setZoom] = useState(MIN_ZOOM)
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 })
  const [isInteracting, setIsInteracting] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    active: boolean
    start: Point
    origin: Point
  }>({ active: false, start: { x: 0, y: 0 }, origin: { x: 0, y: 0 } })
  const pinchRef = useRef<{
    active: boolean
    startDistance: number
    startZoom: number
  }>({ active: false, startDistance: 0, startZoom: MIN_ZOOM })
  const zoomRef = useRef(zoom)
  zoomRef.current = zoom

  const currentSrc = gallery[index] || src || ""
  const hasMultiple = gallery.length > 1

  const resetView = useCallback(() => {
    setZoom(MIN_ZOOM)
    setOffset({ x: 0, y: 0 })
  }, [])

  const applyZoom = useCallback((next: number) => {
    const clamped = clamp(next, MIN_ZOOM, MAX_ZOOM)
    setZoom(clamped)
    if (clamped === MIN_ZOOM) {
      setOffset({ x: 0, y: 0 })
    }
  }, [])

  useEffect(() => {
    if (!open) return
    setIndex(clamp(initialIndex, 0, Math.max(gallery.length - 1, 0)))
    resetView()
  }, [open, initialIndex, gallery.length, resetView])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft" && hasMultiple) {
        setIndex(prev => (prev - 1 + gallery.length) % gallery.length)
        resetView()
      }
      if (e.key === "ArrowRight" && hasMultiple) {
        setIndex(prev => (prev + 1) % gallery.length)
        resetView()
      }
      if (e.key === "+" || e.key === "=") {
        const prev = zoomRef.current
        applyZoom(prev === MIN_ZOOM ? DEFAULT_ZOOM_IN : prev + ZOOM_STEP)
      }
      if (e.key === "-" || e.key === "_") {
        applyZoom(zoomRef.current - ZOOM_STEP)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, onClose, hasMultiple, gallery.length, resetView, applyZoom])

  useEffect(() => {
    if (!open) return
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -ZOOM_STEP / 2 : ZOOM_STEP / 2
      applyZoom(zoomRef.current + delta)
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  }, [open, applyZoom])

  const handleZoomIn = () => {
    applyZoom(zoom === MIN_ZOOM ? DEFAULT_ZOOM_IN : zoom + ZOOM_STEP)
  }

  const handleZoomOut = () => {
    applyZoom(zoom - ZOOM_STEP)
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    if (zoom <= MIN_ZOOM) return
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    setIsInteracting(true)
    dragRef.current = {
      active: true,
      start: { x: e.clientX, y: e.clientY },
      origin: { ...offset }
    }
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current.active || pinchRef.current.active) return
    setOffset({
      x: dragRef.current.origin.x + (e.clientX - dragRef.current.start.x),
      y: dragRef.current.origin.y + (e.clientY - dragRef.current.start.y)
    })
  }

  const handlePointerUp = () => {
    dragRef.current.active = false
    setIsInteracting(false)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      dragRef.current.active = false
      setIsInteracting(true)
      const [a, b] = [e.touches[0], e.touches[1]]
      pinchRef.current = {
        active: true,
        startDistance: getDistance(
          { x: a.clientX, y: a.clientY },
          { x: b.clientX, y: b.clientY }
        ),
        startZoom: zoom
      }
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!pinchRef.current.active || e.touches.length !== 2) return
    e.preventDefault()
    const [a, b] = [e.touches[0], e.touches[1]]
    const distance = getDistance(
      { x: a.clientX, y: a.clientY },
      { x: b.clientX, y: b.clientY }
    )
    if (pinchRef.current.startDistance <= 0) return
    const ratio = distance / pinchRef.current.startDistance
    applyZoom(pinchRef.current.startZoom * ratio)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      pinchRef.current.active = false
      setIsInteracting(false)
    }
  }

  const goPrev = () => {
    setIndex(prev => (prev - 1 + gallery.length) % gallery.length)
    resetView()
  }

  const goNext = () => {
    setIndex(prev => (prev + 1) % gallery.length)
    resetView()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.92)",
          boxShadow: "none"
        }
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          userSelect: "none",
          touchAction: "none"
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "absolute",
            top: { xs: 12, md: 20 },
            right: { xs: 12, md: 20 },
            zIndex: 3
          }}
        >
          <Icons
            name="minus"
            type="button"
            aria-label="zoom out"
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            sx={{
              color: "#000",
              background: "rgba(255,255,255,0.9)",
              "&:hover": { background: "#fff" },
              "&:disabled": { background: "rgba(255,255,255,0.4)", opacity: 0.5 }
            }}
          />
          <Icons
            name="plus"
            type="button"
            aria-label="zoom in"
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            sx={{
              color: "#000",
              background: "rgba(255,255,255,0.9)",
              "&:hover": { background: "#fff" },
              "&:disabled": { background: "rgba(255,255,255,0.4)", opacity: 0.5 }
            }}
          />
          <Icons
            name="close"
            type="button"
            onClick={onClose}
            sx={{
              background: "rgba(255,255,255,0.9)",
              "&:hover": { background: "#fff" }
            }}
          />
        </Stack>

        {hasMultiple && (
          <Stack
            sx={{
              position: "absolute",
              left: { xs: 8, md: 16 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3
            }}
          >
            <Icons
              name="caret-left"
              type="button"
              onClick={goPrev}
              sx={{
                background: "rgba(255,255,255,0.9)",
                "&:hover": { background: "#fff" }
              }}
            />
          </Stack>
        )}

        {hasMultiple && (
          <Stack
            sx={{
              position: "absolute",
              right: { xs: 8, md: 16 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3
            }}
          >
            <Icons
              name="caret-right"
              type="button"
              onClick={goNext}
              sx={{
                background: "rgba(255,255,255,0.9)",
                "&:hover": { background: "#fff" }
              }}
            />
          </Stack>
        )}

        <Box
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: zoom > MIN_ZOOM ? "grab" : "zoom-in",
            "&:active": {
              cursor: zoom > MIN_ZOOM ? "grabbing" : "zoom-in"
            }
          }}
          onDoubleClick={() => {
            if (zoom > MIN_ZOOM) {
              applyZoom(MIN_ZOOM)
            } else {
              applyZoom(DEFAULT_ZOOM_IN)
            }
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={currentSrc}
            alt={alt}
            draggable={false}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              transformOrigin: "center center",
              transition: isInteracting ? "none" : "transform 0.15s ease-out",
              pointerEvents: "none"
            }}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 16, md: 24 },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            px: 1.5,
            py: 0.5,
            borderRadius: "999px",
            background: "rgba(0,0,0,0.55)",
            color: "#fff",
            fontSize: 12,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap"
          }}
        >
          {Math.round(zoom * 100)}%{hasMultiple ? ` · ${index + 1} / ${gallery.length}` : ""}
        </Box>
      </Box>
    </Dialog>
  )
}

export default ProductImageLightbox
